import { Button, DialogActions, DialogContent, Typography } from '@mui/material';
import React, { useState } from 'react';
import { DialogProps } from './DialogProps';
import {
  Application,
  ApplicationForm,
  ApplicationFormConfig,
  ApplicationWithMappedCustomers,
  InvalidCustomerInfo,
  LanguageSelector,
  hasValue,
  isValidHtml,
  languages,
  LookupItem
} from '@amsanalytics/react-shared-library';
import { useSelector } from 'react-redux';
import { IState } from '../../Interfaces/Common/IState';
import { httpPostAsync } from '../../utils/HttpClient';
import { CustomerAddress } from '../../Interfaces/Customer/ICustomerConfig';
import { AddressValidationInfo, AddressValidationResult } from '../../Data/AddressValidationInfo';
import { areStringsEqual } from '../../utils/utils';
import { AppState } from '../../store/rootReducer';
import { authService } from '../..';

// Element that displays the original address and the USPS-validation address. It asks the user if they want to use it or keep the original address.
const AddressValidation = (originalAddress: CustomerAddress, validatedAddressInfo: AddressValidationInfo, states: IState[], onConfirm: (useSuggestedAddress: boolean) => void) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <h2 style={{ margin: 0 }}>Verify Address</h2>
      <p>We found a corrected address based on the information you provided. Please confirm your address below.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-evenly' }}>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, alignItems: 'center' }}>
          <div style={{ fontWeight: 600, marginBottom: '3px' }}>You Entered</div>
          <div style={{ textAlign: 'center', width: '100%', wordBreak: 'break-word' }}>
            {originalAddress.address?.trim()}<br /> {hasValue(originalAddress.address2?.trim()) && <>{originalAddress.address2?.trim()}<br /></>} {originalAddress.city?.trim()}, {states.find(s => s.id === originalAddress.stateId)?.abbreviation}, {originalAddress.zip}
          </div>
          <Button
            variant='outlined'
            color='primary'
            style={{ textTransform: 'none', marginTop: '10px' }}
            onClick={() => onConfirm(false)}
          >
            Use My Entry
          </Button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, alignItems: 'center' }}>
          <div style={{ fontWeight: 600, marginBottom: '3px' }}>Suggested</div>
          <div style={{ textAlign: 'center', width: '100%', wordBreak: 'break-word' }}>
            {validatedAddressInfo.address1?.trim()}<br /> {hasValue(validatedAddressInfo.address2?.trim()) && <>{validatedAddressInfo.address2?.trim()}<br /></>} {validatedAddressInfo.city?.trim()}, {validatedAddressInfo.state}, {validatedAddressInfo.zipCode}{hasValue(validatedAddressInfo.zipExtension) && <>-{validatedAddressInfo.zipExtension}</>}
          </div>
          <Button
            variant='contained'
            color='primary'
            style={{ textTransform: 'none', marginTop: '10px' }}
            onClick={() => onConfirm(true)}
          >
            Use Suggested
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface ApplicationFormDialogProps extends DialogProps<Application> {
  applicantCreditAppFormConfiguration: ApplicationFormConfig;
  coApplicantCreditAppFormConfiguration: ApplicationFormConfig;
  defaultApplication: ApplicationWithMappedCustomers;
  maxNumberOfApplicants: number;
  requireSignatureOnForm: boolean; // whether or not the application form requires a signature
  shouldValidateSsn?: boolean; // whether or not to validate SSN, based on client feature flag
  shouldValidateAddress?: boolean; // whether or not to validate customer address, based on client feature flag
}

export default function ApplicationFormDialog(props: ApplicationFormDialogProps) {
  const [application, setApplication] = useState<ApplicationWithMappedCustomers>(props.defaultApplication);
  // Whether or not user has clicked 'Save' button.
  const [saveButtonClicked, setSaveButtonClicked] = useState<boolean>(false);
  // List of marital statuses user can select from.
  const maritalStatuses: LookupItem[] = useSelector((state: AppState) => state.applications.maritalStatuses);
  // List of states user can select from.
  const states: IState[] = useSelector((state: AppState) => state.applications.states);
  //List of identification types
  const identificationTypes = useSelector((appState: AppState) => appState.applications.lookups.identificationTypes);
  // list of IDs to filter the list of applicant addresses to show in the dropdown
  const applicantAddressStateIdsToFilterBy = useSelector((appState: AppState) => appState.applications.lookups.applicantAddressStateIdsToFilterBy);
  // list of IDs to filter the list of employment addresses to show in the dropdown
  const employerAddressStateIdsToFilterBy = useSelector((appState: AppState) => appState.applications.lookups.employerAddressStateIdsToFilterBy);
  // list of IDs to filter the list of driver license addresses to show in the dropdown
  const driverLicenseStateIdsToFilterBy = useSelector((appState: AppState) => appState.applications.lookups.driverLicenseStateIdsToFilterBy);
  // List of housing statuses user can select from.
  const housingStatuses: LookupItem[] = useSelector((state: AppState) => state.applications.housingStatuses);
  // List of income periods user can select from.
  const incomePeriods: LookupItem[] = useSelector((state: AppState) => state.applications.incomePeriods);
  // List of income types the user can select from.
  const incomeTypes: LookupItem[] = useSelector((state: AppState) => state.applications.incomeTypes);
  // Minimum months of address
  const minMonthsAddress = useSelector((appState: AppState) => appState.applications.lookups.minimumMonthsOfAddressInformation);
  // Minimum months of employment
  const minMonthsEmployment = useSelector((appState: AppState) => appState.applications.lookups.minimumMonthsOfEmploymentInformation);
  // List of invalid application customers
  const [invalidCustomers, setInvalidCustomers] = useState<InvalidCustomerInfo[]>([]);
  // Get disclosure text from lookup items
  const disclosureText = useSelector((state: AppState) => state.applications.lookups.disclosure);
  // Spanish disclosure text
  const spanishDisclosureText = useSelector((state: AppState) => state.applications.lookups.spanishDisclosure);
  // privacy policy url
  const privacyPolicyUrl = useSelector((state: AppState) => state.applications.lookups.privacyPolicyUrl);
  // terms and conditions url
  const termsAndConditionsUrl = useSelector((state: AppState) => state.applications.lookups.termsAndConditionsUrl);
  const customFields = useSelector((state: AppState) => state.applications.customFields);
  const relationshipToApplicantTypes = useSelector((appState: AppState) => appState.applications.lookups.relationshipToApplicants);
  // Whether or not this application form is valid
  const hasErrors = invalidCustomers && invalidCustomers?.length > 0;
  // whether or not to show the "USPS Validate Address" element
  const [addressValidationInfo, setAddressValidationInfo] = useState<AddressValidationInfo>({ show: false });
  // Language translations
  const languageTranslations = useSelector((state: AppState) => state.applications.lookups.languageTranslations);
  // Whether or not the user can toggle between English and Spanish
  const allowSpanishTranslation = useSelector((state: AppState) => state.applications.lookups.allowSpanishTranslation);
  // Selected language id. 1 - English, 2 - Spanish
  const [selectedLanguageId, setSelectedLanguageId] = useState<string>('1');

  // Returns true if the current address and the suggested address are the same.
  const isCurrentAndSuggestedAddressesEqual = (validatedAddressInfo: AddressValidationResult, customerMappingId: number): boolean => {
    const customerPrimaryAddress = application && application.customers.find(c => c.mappingId === customerMappingId)?.addresses?.find(a => a.isPrimary === true);
    return (
      areStringsEqual(customerPrimaryAddress?.address, validatedAddressInfo.address1) &&
      areStringsEqual(customerPrimaryAddress?.address2, validatedAddressInfo.address2) &&
      areStringsEqual(customerPrimaryAddress?.city, validatedAddressInfo.city) &&
      areStringsEqual(customerPrimaryAddress?.stateId?.toString(), states.find(s => s.abbreviation?.toUpperCase() === validatedAddressInfo.state?.toUpperCase())?.id?.toString()) &&
      areStringsEqual(customerPrimaryAddress?.zip?.toString(), validatedAddressInfo.zipCode?.toString())
    );
  }

  // Gets the validated address for the customer with the given mapping id
  const getValidatedAddress = async (customerMappingId: number) => {
    // Get the bearer token
    const bearerToken = await authService.getToken();
    const customerPrimaryAddress = application.customers.find(c => c.mappingId === customerMappingId)?.addresses?.find(a => a.isPrimary === true);
    // get the address information to validate
    const addressToValidate: Omit<AddressValidationInfo, "show" | "customerMappingId" | "addressMappingId"> = {
      address1: customerPrimaryAddress?.address,
      address2: customerPrimaryAddress?.address2,
      city: customerPrimaryAddress?.city,
      state: states.find(s => s.id === customerPrimaryAddress?.stateId)?.abbreviation,
      zipCode: customerPrimaryAddress?.zip?.toString(),
    };
    // Post the request to validate the address and capture the updated address
    const validatedAddress: Response | undefined = await httpPostAsync(
      `api/AddressValidation/validate`,
      addressToValidate,
      bearerToken!
    );
    return validatedAddress;
  }

  // async post method for validating the address for the customer with the given mapping id
  const validateAddress = async (customerMappingId: number) => {
    await getValidatedAddress(customerMappingId).then((response) => {
      // If ok response, then set the address validation info so that the user can confirm the address.
      // If not ok response, then do nothing.
      if (response && response.ok) {
        response.json().then((validatedAddressInfo: AddressValidationResult) => {
          if (validatedAddressInfo.hasError !== true) {
            // only set the address validation info if the suggested address is different than the original address.
            if (!isCurrentAndSuggestedAddressesEqual(validatedAddressInfo, customerMappingId)) {
              setAddressValidationInfo({ show: true, customerMappingId: customerMappingId, addressMappingId: 0, ...validatedAddressInfo });
            }
          }
        });
      }
    });
  };

  // Get the dialog title based on the selected language and whether or not we are editing an existing application
  const getDialogTitle = () => {
    if (selectedLanguageId === '1') {
      return application.id ? "Edit Application" : "New Application";
    }
    return application.id ? "Editar Solicitud" : "Nueva Solicitud";
  }

  return (
    <>
      <h1 style={{ flex: `0 0 auto`, margin: `0`, padding: `16px 24px` }}>{getDialogTitle()}</h1>
      <DialogContent>
        {/* {addressValidationInfo.show === true && <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'white', padding: '20px', borderRadius: '5px' }}>
            {AddressValidation(
              application.customers.find(c => c.mappingId === addressValidationInfo.customerMappingId)!.addresses[0],
              addressValidationInfo,
              states,
              (useSuggestedAddress: boolean) => {
                // get the customer to update
                const customer = application.customers.find(c => c.mappingId === addressValidationInfo.customerMappingId);
                // if the user wants to use the suggested address, then update the customer address
                if (useSuggestedAddress && customer) {
                  const newAddress = {
                    ...customer.addresses.find(a => a.mappingId === addressValidationInfo.addressMappingId)!,
                    address: addressValidationInfo.address1,
                    address2: addressValidationInfo.address2,
                    city: addressValidationInfo.city,
                    stateId: hasValue(addressValidationInfo.state) ? getStateByAbbreviation(addressValidationInfo.state!, states)?.id : undefined,
                    zip: addressValidationInfo.zipCode,
                  };
                  const newApp: ApplicationWithMappedCustomers = {
                    ...application,
                    customers: application.customers.map(c => {
                      if (c.mappingId === addressValidationInfo.customerMappingId) {
                        return {
                          ...c,
                          addresses: c.addresses.map(a => {
                            if (a.mappingId === newAddress.mappingId) {
                              return {
                                ...newAddress
                              };
                            }
                            return a;
                          })
                        };
                      } else {
                        return c;
                      }
                    }),
                  };
                  setApplication(newApp);
                }
                setAddressValidationInfo(emptyAddressValidationInfo);
              })}
          </div>
        </div>} */}
        {allowSpanishTranslation && (
          <LanguageSelector
            languages={languages}
            selectedLanguageId={selectedLanguageId}
            label={selectedLanguageId === '1' ? 'Preferred Language' : 'Idioma Preferido'}
            onSelectLanguage={(languageId: string) => {
              // update browser tab title based on the selected language
              document.title = languageId === '1' ? 'Customer Credit Application' : 'Solicitud de Crédito del Cliente';
              setSelectedLanguageId(languageId);
            }}
          />
        )}
        <ApplicationForm
          application={application}
          applicantCreditAppFormConfiguration={props.applicantCreditAppFormConfiguration}
          coApplicantCreditAppFormConfiguration={props.coApplicantCreditAppFormConfiguration}
          invalidCustomers={invalidCustomers}
          requireSignature={!props.requireSignatureOnForm}
          identificationTypes={identificationTypes}
          allowSubmission={invalidCustomers.length === 0 || invalidCustomers.every(c => c.errors.length === 0)}
          saveProgress={false}
          saveButtonClicked={saveButtonClicked}
          customFields={customFields}
          housingStatuses={housingStatuses}
          incomePeriods={incomePeriods}
          relationshipTypes={relationshipToApplicantTypes}
          maritalStatuses={maritalStatuses}
          maxNumberOfApplicants={props.maxNumberOfApplicants}
          minimumMonthsOfAddressInformation={minMonthsAddress}
          minimumMonthsOfEmploymentInformation={minMonthsEmployment}
          incomeTypes={incomeTypes}
          states={states}
          selectedLanguageId={selectedLanguageId}
          languageTranslations={languageTranslations}
          applicantAddressStateIdsToFilterBy={applicantAddressStateIdsToFilterBy}
          employerAddressStateIdsToFilterBy={employerAddressStateIdsToFilterBy}
          driverLicenseStateIdsToFilterBy={driverLicenseStateIdsToFilterBy}
          shouldValidateSSN={props.shouldValidateSsn}
          privacyNoticeUrl={privacyPolicyUrl}
          termsAndConditionsUrl={termsAndConditionsUrl}
          onChange={(application: ApplicationWithMappedCustomers) => {
            setApplication(application);
          }}
          onSetErrors={(invalidCustomers?: InvalidCustomerInfo[]) => {
            setInvalidCustomers(invalidCustomers || []);
          }}
          onSubmit={() => {
            setSaveButtonClicked(true);
            if (!hasErrors) {
              props.accept && props.accept(application);
            }
          }}
          onAddressNextClicked={(customerMappingId: number) => {
            // If client has enabled address validation, then validate the address.
            if (props.shouldValidateAddress === true) {
              validateAddress(customerMappingId);
            }
          }}
        />
        {!props.requireSignatureOnForm &&
          <div style={{ display: 'flex', flexDirection: 'column', padding: '10px' }}>
            {isValidHtml(disclosureText) ? <div dangerouslySetInnerHTML={{ __html: disclosureText }} /> :
              <Typography style={{ fontSize: '13px', fontWeight: 'bold', color: '#565656' }}>
                {selectedLanguageId === '1' ? disclosureText : spanishDisclosureText ?? disclosureText}
              </Typography>
            }
          </div>
        }
      </DialogContent>
      <DialogActions>
        {saveButtonClicked && hasErrors && (
          <span style={{ color: `red`, fontStyle: `bold` }}>Please complete all required fields.</span>
        )}
        <Button autoFocus onClick={props.closeDialog} color='primary' variant='outlined'>
          {selectedLanguageId === '1' ? 'Cancel' : 'Cancelar'}
        </Button>
      </DialogActions>
    </>
  );
}
